import { useEffect, useMemo, useState } from 'react';
import {
  AttributionCategory,
  AttributionCategoryParentEnum,
} from '@frontend/pricing-search';
import { generateOptionsWithId } from '@frontend/utils';
import { useAttributionCategories } from '../../../data/useSelectors';

const adjustmentTypeMap = {
  ReLockFeeAdjustment: AttributionCategoryParentEnum.General,
  CustomPriceAdjustment: AttributionCategoryParentEnum.General,
  CustomRateAdjustment: AttributionCategoryParentEnum.General,
  BranchPriceConcession: AttributionCategoryParentEnum.Branch,
  CorporatePriceConcession: AttributionCategoryParentEnum.Corporate,
};

export const useDescription = (rowCard) => {
  const allCategories = useAttributionCategories();
  const generateAdjustmentOptions = (categories: AttributionCategory[]) => {
    const filteredByAdjustmentType = categories.filter((category) => {
      if (adjustmentTypeMap[rowCard.adjustmentType])
        return category.parent?.includes(
          adjustmentTypeMap[rowCard.adjustmentType],
        );
      return false;
    });
    return generateOptionsWithId(
      filteredByAdjustmentType.map((item) => ({
        label: item.name,
        value: item.guid,
      })),
    );
  };
  const paidByOptions = useMemo(
    () => generateAdjustmentOptions(allCategories?.paidByCategories ?? []),
    [allCategories],
  );
  const adjustmentReasonsOptions = useMemo(
    () =>
      generateAdjustmentOptions(
        allCategories?.adjustmentReasonCategories ?? [],
      ),
    [allCategories],
  );

  const [selectedPaidByValue, setSelectedPaidByValue] = useState(
    paidByOptions.find((i) => i.value === rowCard.paidByGuid) ?? {},
  );
  const [selectedAdjustmentReasonValue, setSelectedAdjustmentReasonValue] =
    useState(
      adjustmentReasonsOptions.find(
        (i) => i.value === rowCard.adjustmentReasonGuid,
      ) ?? {},
    );

  const checkIfObjectIsEmpty = (obj) => {
    return Object.keys(obj)?.length === 0;
  };

  useEffect(() => {
    // Set these as null if there are no options, so that the requirement validator knows to skip them
    if (!paidByOptions.length) {
      rowCard.paidByGuid = null;
    }
    // this handles the case when the options are loaded but there is no matching value, so that the validator can pick it up
    if (paidByOptions.length && checkIfObjectIsEmpty(selectedPaidByValue)) {
      rowCard.paidByGuid = undefined;
    }
  }, [paidByOptions, selectedPaidByValue]);
  useEffect(() => {
    // Set these as null if there are no options, so that the requirement validator knows to skip them
    if (!adjustmentReasonsOptions.length) {
      rowCard.adjustmentReasonGuid = null;
    }
    // this handles the case when the options are loaded but there is no matching value, so that the validator can pick it up
    if (
      adjustmentReasonsOptions.length &&
      checkIfObjectIsEmpty(selectedAdjustmentReasonValue)
    ) {
      rowCard.adjustmentReasonGuid = undefined;
    }
  }, [adjustmentReasonsOptions, selectedAdjustmentReasonValue]);

  const memoizedValues = useMemo(() => {
    return {
      selectedPaidByValue,
      setSelectedPaidByValue,
      selectedAdjustmentReasonValue,
      setSelectedAdjustmentReasonValue,
      paidByOptions,
      adjustmentReasonsOptions,
      rowCard,
    };
  }, [rowCard]);
  return memoizedValues;
};
